/**
 * Pure CRM rules: phone numbers, CDR legs → calls, and missed-call recovery. No database access,
 * so the import, the migration checks and the tests all use the same definitions.
 */

export type PbxCdrRecord = {
  call_from?: string;
  call_to?: string;
  call_from_number?: string;
  call_to_number?: string;
  call_from_name?: string;
  call_to_name?: string;
  time?: string;
  timestamp?: number;
  disposition?: string;
  talk_duration?: number;
  ring_duration?: number;
  call_type?: 'Inbound' | 'Outbound' | 'Internal';
  id?: string | number;
  uid?: string | number;
  new_id?: string | number;
  call_id?: string;
  recording?: string;
};

export function normalizePhoneNumber(phone: string) {
  if (!phone) return '';
  const trimmed = extractNumber(phone).trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
}

/** Index keys for a number: as typed, South African national/international forms, and suffixes. */
export function phoneCandidates(phone: string) {
  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return [];

  const candidates = new Set<string>([normalized]);
  candidates.add(normalized.replace(/^\+27/, '0'));
  candidates.add(normalized.replace(/^0/, '+27'));
  candidates.add(normalized.slice(-10));
  candidates.add(normalized.slice(-9));
  return Array.from(candidates).filter(Boolean);
}

/** Yeastar writes some CDR parties as `Name<number>`; the number is the part in angle brackets. */
export function extractNumber(value: string) {
  const match = /<([^<>]*)>\s*$/.exec(value ?? '');
  return (match ? match[1] : value ?? '').trim();
}

const NOT_A_NAME = /^(unknown|anonymous|private|unavailable|restricted|withheld|admin|none|null|undefined)$/i;

/**
 * The name sent with an incoming number: by the caller's network (caller ID name) or from the PBX
 * phone book. Yeastar gives it as `Name<number>` or as call_from_name. Digits-only values, the
 * number itself and placeholders like "Anonymous" are not names.
 */
export function callerIdName(value: string | undefined, number = '') {
  const raw = (value ?? '').trim();
  const name = (/^(.*)<[^<>]*>\s*$/.exec(raw)?.[1] ?? (/<[^<>]*>/.test(raw) ? '' : raw))
    .split(/\s+/)
    .filter((word) => !/^\+?\d{7,}$/.test(word) && word !== number)
    .join(' ')
    .trim();
  if (!/\p{L}/u.test(name) || NOT_A_NAME.test(name)) return undefined;
  return name.slice(0, 100);
}

/**
 * One spelling per number, for grouping calls and callbacks: South African numbers in national
 * form (0XXXXXXXXX) whether they arrive as +27…, 27… or 0…; anything else as normalised.
 */
export function canonicalPhone(phone: string) {
  const normalized = normalizePhoneNumber(phone);
  const digits = normalized.replace(/^\+/, '');
  if (/^27\d{9}$/.test(digits)) return `0${digits.slice(2)}`;
  if (/^\d{9}$/.test(digits) && !digits.startsWith('0')) return `0${digits}`;
  return normalized;
}

export function contactSortName(name: string) {
  return name.trim().toLowerCase();
}

export function buildSearchText(contact: {
  name: string;
  company?: string;
  email?: string;
  jobTitle?: string;
  phones: Array<{ number: string }>;
  tags?: string[];
}) {
  const numbers = new Set<string>();
  for (const phone of contact.phones) {
    const normalized = normalizePhoneNumber(phone.number);
    if (!normalized) continue;
    numbers.add(normalized);
    numbers.add(canonicalPhone(normalized));
  }
  return [contact.name, contact.company, contact.email, contact.jobTitle, ...(contact.tags ?? []), ...numbers]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeTags(tags: string[] | undefined) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const tag of tags ?? []) {
    const clean = tag.trim().replace(/\s+/g, ' ').slice(0, 40);
    if (!clean || seen.has(clean.toLowerCase())) continue;
    seen.add(clean.toLowerCase());
    result.push(clean);
  }
  return result.slice(0, 20);
}

/**
 * What answered or placed a leg. Yeastar names system destinations in call_to, e.g.
 * "IVR IVR-Welcome<6200>" or "Queue CQ-Sales<6402>". An IVR "answers" every call it plays to,
 * so an IVR leg never makes a call answered; a queue or ring group answering means an agent did.
 */
export type LegParty = 'person' | 'group' | 'system';

const GROUP_LEG = /^(queue|ring ?group)\b/i;
const SYSTEM_LEG = /^(ivr|conference|paging|intercom|call ?flow|voicemail|dial ?by ?name|fax)\b/i;

export function legParty(field: string | undefined): LegParty {
  const value = (field ?? '').trim();
  if (SYSTEM_LEG.test(value)) return 'system';
  if (GROUP_LEG.test(value)) return 'group';
  return 'person';
}

export type CdrLeg = {
  callUid: string;
  party: LegParty;
  callKey: string;
  direction: 'inbound' | 'outbound';
  externalNumber: string;
  normalizedExternalNumber: string;
  canonicalNumber: string;
  /** The extension that rang (inbound) or dialled (outbound). */
  extension?: string;
  extensionName?: string;
  /** Inbound: the name that came with the caller's number (see callerIdName). */
  callerIdName?: string;
  disposition: string;
  answered: boolean;
  voicemail: boolean;
  talkSeconds: number;
  timestamp: number;
  time: string;
};

export function cdrTimestamp(record: PbxCdrRecord) {
  if (typeof record.timestamp === 'number' && Number.isFinite(record.timestamp)) {
    return record.timestamp > 10_000_000_000 ? Math.floor(record.timestamp / 1000) : record.timestamp;
  }
  const parsed = Date.parse(record.time || '');
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : Math.floor(Date.now() / 1000);
}

export function cdrDedupeKey(record: PbxCdrRecord) {
  const explicit = record.new_id || record.id || record.uid;
  if (explicit) return String(explicit);
  return [
    record.time || '',
    record.call_from || '',
    record.call_to || '',
    record.call_type || '',
    record.disposition || '',
    record.talk_duration || 0,
  ].join('|');
}

/** A voicemail is a missed call for the business, even though the PBX "answered" it. */
export function isAnsweredLeg(disposition: string, talkSeconds: number) {
  const status = disposition.toUpperCase();
  if (status === 'VOICEMAIL') return false;
  return status === 'ANSWERED' || talkSeconds > 0;
}

export function legFromCdr(record: PbxCdrRecord): CdrLeg | null {
  const callType = record.call_type || 'Internal';
  if (callType !== 'Inbound' && callType !== 'Outbound') return null;
  const inbound = callType === 'Inbound';

  const externalNumber = extractNumber(
    (inbound ? record.call_from_number : record.call_to_number) || (inbound ? record.call_from : record.call_to) || ''
  );
  const normalizedExternalNumber = normalizePhoneNumber(externalNumber);
  if (!normalizedExternalNumber) return null;

  const extensionField = inbound ? record.call_to : record.call_from;
  const extension = extractNumber((inbound ? record.call_to_number : record.call_from_number) || extensionField || '') || undefined;
  const nameField = (inbound ? record.call_to_name : record.call_from_name) || /^(.*)<[^<>]*>\s*$/.exec(extensionField || '')?.[1];
  const disposition = String(record.disposition || '').toUpperCase();
  const talkSeconds = Number(record.talk_duration || 0);
  const timestamp = cdrTimestamp(record);
  const canonicalNumber = canonicalPhone(normalizedExternalNumber);
  const callUid = String(record.uid || record.call_id || cdrDedupeKey(record));
  const party = inbound ? legParty(record.call_to) : 'person';

  return {
    callUid,
    party,
    callKey: `${callUid}:${canonicalNumber}`,
    direction: inbound ? 'inbound' : 'outbound',
    externalNumber,
    normalizedExternalNumber,
    canonicalNumber,
    extension,
    extensionName: nameField?.trim() || undefined,
    callerIdName: inbound ? callerIdName(record.call_from_name || record.call_from, externalNumber) : undefined,
    disposition,
    answered: party !== 'system' && isAnsweredLeg(disposition, talkSeconds),
    voicemail: disposition === 'VOICEMAIL',
    talkSeconds,
    timestamp,
    time: record.time || new Date(timestamp * 1000).toISOString(),
  };
}

export type CallRollup = {
  callKey: string;
  externalNumber: string;
  normalizedExternalNumber: string;
  direction: 'inbound' | 'outbound';
  answered: boolean;
  voicemail: boolean;
  startedAtMs: number;
  talkSeconds: number;
  answeredBy?: string;
  answeredByName?: string;
  extensions: string[];
  legCount: number;
  disposition: string;
};

/** All legs of one call with one outside party, as one call. */
export function rollupLegs(legs: CdrLeg[]): CallRollup {
  if (legs.length === 0) throw new Error('rollupLegs needs at least one leg');
  const ordered = [...legs].sort((a, b) => a.timestamp - b.timestamp);
  const first = ordered[0];
  const answeredLegs = ordered.filter((leg) => leg.answered);
  // Who answered: the person leg with the most talk, else the queue or ring group.
  const byTalk = (a: CdrLeg, b: CdrLeg) => b.talkSeconds - a.talkSeconds;
  const best = [...answeredLegs.filter((leg) => leg.party === 'person')].sort(byTalk)[0] ?? [...answeredLegs].sort(byTalk)[0];
  const answered = answeredLegs.length > 0;
  const voicemail = !answered && ordered.some((leg) => leg.voicemail);
  const extensions = Array.from(
    new Set(ordered.filter((leg) => leg.party === 'person').map((leg) => leg.extension).filter((value): value is string => Boolean(value)))
  ).slice(0, 20);

  return {
    callKey: first.callKey,
    externalNumber: first.externalNumber,
    normalizedExternalNumber: first.canonicalNumber,
    direction: first.direction,
    answered,
    voicemail,
    startedAtMs: first.timestamp * 1000,
    talkSeconds: best ? best.talkSeconds : 0,
    answeredBy: best?.extension,
    answeredByName: best?.extensionName,
    extensions,
    legCount: ordered.length,
    disposition: answered ? 'ANSWERED' : voicemail ? 'VOICEMAIL' : ordered[ordered.length - 1].disposition,
  };
}

export type StatsCounters = {
  totalInteractions: number;
  receivedCalls: number;
  madeCalls: number;
  missedCalls: number;
  answeredCalls: number;
  totalDuration: number;
};

/** What one call adds to its contact's counters. Missed means an inbound call nobody answered. */
export function statsForCall(call: Pick<CallRollup, 'direction' | 'answered' | 'talkSeconds'> | null): StatsCounters {
  if (!call) {
    return { totalInteractions: 0, receivedCalls: 0, madeCalls: 0, missedCalls: 0, answeredCalls: 0, totalDuration: 0 };
  }
  return {
    totalInteractions: 1,
    receivedCalls: call.direction === 'inbound' && call.answered ? 1 : 0,
    madeCalls: call.direction === 'outbound' && call.answered ? 1 : 0,
    missedCalls: call.direction === 'inbound' && !call.answered ? 1 : 0,
    answeredCalls: call.answered ? 1 : 0,
    totalDuration: call.talkSeconds,
  };
}

export type RecoveryCall = {
  partyKey: string;
  direction: 'inbound' | 'outbound';
  answered: boolean;
  startedAtMs: number;
  answeredBy?: string;
  answeredByName?: string;
};

export type MissedEpisode = {
  partyKey: string;
  firstMissedAtMs: number;
  lastMissedAtMs: number;
  missedCount: number;
  attempts: number;
  outcome: 'called_back' | 'caller_reached' | 'not_recovered' | 'pending';
  recoveredAtMs?: number;
  recoveredBy?: string;
  recoveredByName?: string;
};

/**
 * Missed-call episodes for every party: from the first unanswered inbound call to the next
 * answered call either way. Recovered within `horizonMs` counts; episodes younger than the horizon
 * that are still open are pending rather than lost.
 */
export function missedEpisodes(calls: RecoveryCall[], nowMs: number, horizonMs: number): MissedEpisode[] {
  const byParty = new Map<string, RecoveryCall[]>();
  for (const call of calls) {
    const list = byParty.get(call.partyKey) ?? [];
    list.push(call);
    byParty.set(call.partyKey, list);
  }

  const episodes: MissedEpisode[] = [];
  for (const [partyKey, partyCalls] of byParty) {
    partyCalls.sort((a, b) => a.startedAtMs - b.startedAtMs);
    let open: MissedEpisode | null = null;
    for (const call of partyCalls) {
      if (call.direction === 'inbound' && !call.answered) {
        if (open && call.startedAtMs - open.firstMissedAtMs <= horizonMs) {
          open.missedCount += 1;
          open.lastMissedAtMs = call.startedAtMs;
        } else {
          if (open) episodes.push({ ...open, outcome: 'not_recovered' });
          open = { partyKey, firstMissedAtMs: call.startedAtMs, lastMissedAtMs: call.startedAtMs, missedCount: 1, attempts: 0, outcome: 'pending' };
        }
        continue;
      }
      if (!open) continue;
      if (call.startedAtMs - open.firstMissedAtMs > horizonMs) {
        episodes.push({ ...open, outcome: 'not_recovered' });
        open = null;
        continue;
      }
      if (call.direction === 'outbound' && !call.answered) {
        open.attempts += 1;
        continue;
      }
      if (call.answered) {
        episodes.push({
          ...open,
          outcome: call.direction === 'outbound' ? 'called_back' : 'caller_reached',
          recoveredAtMs: call.startedAtMs,
          recoveredBy: call.answeredBy,
          recoveredByName: call.answeredByName,
        });
        open = null;
      }
    }
    if (open) {
      episodes.push({ ...open, outcome: nowMs - open.firstMissedAtMs > horizonMs ? 'not_recovered' : 'pending' });
    }
  }
  return episodes;
}

export function median(values: number[]) {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export type RecoveryReport = {
  missedCalls: number;
  missedCallers: number;
  calledBack: number;
  callerReached: number;
  notRecovered: number;
  pending: number;
  recoveryRate: number | null;
  medianMinutesToRecover: number | null;
  withinSla: number;
  byExtension: Array<{ extension: string; name?: string; recovered: number }>;
};

export function recoveryReport(episodes: MissedEpisode[], slaMinutes: number): RecoveryReport {
  const recovered = episodes.filter((episode) => episode.outcome === 'called_back' || episode.outcome === 'caller_reached');
  const decided = episodes.filter((episode) => episode.outcome !== 'pending');
  const minutes = recovered.map((episode) => ((episode.recoveredAtMs ?? 0) - episode.firstMissedAtMs) / 60_000);
  const byExtension = new Map<string, { extension: string; name?: string; recovered: number }>();
  for (const episode of recovered) {
    if (episode.outcome !== 'called_back' || !episode.recoveredBy) continue;
    const row = byExtension.get(episode.recoveredBy) ?? { extension: episode.recoveredBy, name: episode.recoveredByName, recovered: 0 };
    row.recovered += 1;
    byExtension.set(episode.recoveredBy, row);
  }
  const middle = median(minutes);
  return {
    missedCalls: episodes.reduce((total, episode) => total + episode.missedCount, 0),
    missedCallers: episodes.length,
    calledBack: episodes.filter((episode) => episode.outcome === 'called_back').length,
    callerReached: episodes.filter((episode) => episode.outcome === 'caller_reached').length,
    notRecovered: episodes.filter((episode) => episode.outcome === 'not_recovered').length,
    pending: episodes.filter((episode) => episode.outcome === 'pending').length,
    recoveryRate: decided.length ? recovered.length / decided.length : null,
    medianMinutesToRecover: middle === undefined ? null : Math.round(middle),
    withinSla: minutes.filter((value) => value <= slaMinutes).length,
    byExtension: Array.from(byExtension.values()).sort((a, b) => b.recovered - a.recovered).slice(0, 10),
  };
}

/** One account's calls over the insight window, for the insight rules. */
export type InsightSubject = {
  key: string;
  label: string;
  /** Call start times (ms), any direction. */
  callTimes: number[];
  missedTimes: number[];
  /** Answered calls and talk seconds per extension over the last 90 days. */
  talkByExtension: Record<string, { seconds: number; calls?: number; name?: string }>;
  ownerExtension?: string;
};

export type InsightFinding = {
  kind: 'going_quiet' | 'call_spike' | 'missed_often' | 'owner_suggestion';
  title: string;
  detail: string;
  suggestedExtension?: string;
  suggestedName?: string;
};

const DAY = 86_400_000;
const WEEK = 7 * DAY;

function days(count: number) {
  return count === 1 ? 'day' : `${count} days`;
}

export function insightFindings(subject: InsightSubject, nowMs: number): InsightFinding[] {
  const findings: InsightFinding[] = [];
  const times = [...subject.callTimes].sort((a, b) => a - b);
  const last = times[times.length - 1];

  // Going quiet: a regular (calls in 4+ different weeks of the last 4 months) silent for 3x its usual
  // gap between days with calls, and at least 21 days.
  const history = times.filter((time) => time >= nowMs - 120 * DAY);
  const activeWeeks = new Set(history.map((time) => Math.floor(time / WEEK))).size;
  if (activeWeeks >= 4 && last !== undefined) {
    const callDays = Array.from(new Set(history.map((time) => Math.floor(time / DAY)))).sort((a, b) => a - b);
    const gaps = callDays.slice(1).map((day, index) => (day - callDays[index]) * DAY);
    const usualGap = median(gaps) ?? WEEK;
    const silentFor = nowMs - last;
    if (silentFor >= Math.max(21 * DAY, 3 * usualGap)) {
      const usualDays = Math.max(1, Math.round(usualGap / DAY));
      findings.push({
        kind: 'going_quiet',
        title: `${subject.label} has gone quiet`,
        detail: `Called in ${activeWeeks} different weeks over 4 months, usually every ${days(usualDays)}; nothing for ${Math.round(silentFor / DAY)} days.`,
      });
    }
  }

  // Call spike: last 7 days at least 5 calls and 3x the weekly average of the 8 weeks before.
  const lastWeek = times.filter((time) => time >= nowMs - WEEK).length;
  const before = times.filter((time) => time < nowMs - WEEK && time >= nowMs - 9 * WEEK).length;
  const weeklyAverage = before / 8;
  if (lastWeek >= 5 && lastWeek >= 3 * Math.max(weeklyAverage, 1)) {
    findings.push({
      kind: 'call_spike',
      title: `More calls than usual with ${subject.label}`,
      detail: `${lastWeek} calls in the last 7 days, against ${
        weeklyAverage < 0.5 ? 'hardly any before' : `about ${Math.round(weeklyAverage)} a week before`
      }. Worth checking all is well.`,
    });
  }

  // Missed often: 3+ missed calls in 14 days.
  const recentMisses = subject.missedTimes.filter((time) => time >= nowMs - 14 * DAY).length;
  if (recentMisses >= 3) {
    findings.push({
      kind: 'missed_often',
      title: `${subject.label}'s calls are often missed`,
      detail: `${recentMisses} missed calls in the last 14 days.`,
    });
  }

  // Owner suggestion: no owner yet, and one extension holds most (>= 60%) of 30+ minutes of talk over 3+ calls.
  if (!subject.ownerExtension) {
    const entries = Object.entries(subject.talkByExtension);
    const total = entries.reduce((sum, [, row]) => sum + row.seconds, 0);
    const [topExtension, top] = entries.sort((a, b) => b[1].seconds - a[1].seconds)[0] ?? [];
    if (topExtension && top && total >= 1800 && top.seconds / total >= 0.6 && (top.calls ?? 3) >= 3) {
      const who = top.name ? `${top.name} (${topExtension})` : `extension ${topExtension}`;
      findings.push({
        kind: 'owner_suggestion',
        title: `${who} looks after ${subject.label}`,
        detail: `${Math.round((top.seconds / total) * 100)}% of ${Math.round(total / 60)} minutes of talk over the last 90 days${top.calls ? `, ${top.calls} calls` : ''}.`,
        suggestedExtension: topExtension,
        suggestedName: top.name,
      });
    }
  }

  return findings;
}
